import { editBanner } from '@/entities/banners/model/bannerSlice';
import {
  ActionType,
  ButtonType,
  IBanner,
} from '@/entities/banners/types/IBanner';
import { useNotification } from '@/shared/lib/hooks/useNotification';
import { useAppDispatch } from '@/shared/model';
import { Checkbox as CustomCheckbox } from '@/shared/ui/Checkbox/Checkbox';
import DateInput from '@/shared/ui/DateInput';
import GoBack from '@/shared/ui/GoBack/GoBack';
import { Input } from '@/shared/ui/Input/Input';
import { Select } from '@/shared/ui/Select/Select';
import { TextArea } from '@/shared/ui/TextArea/TextArea';
import TimeInput from '@/shared/ui/TimeInput';
import Header from '@/widgets/Header/Header';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { InputBannerImage } from '../components/InputBannerImage';
import { InputModalImage } from '../components/InputModalImage';
import {
  ACTION_OPTIONS,
  BUTTON_TYPE_OPTIONS,
  EditBannerForm,
  getInitialSelectOption,
  parseDateTime,
  STATUS_OPTIONS,
  TEXT_COLOR_OPTIONS,
  toISODate,
} from '../constants';

type SelectOption = { id: string | number; title: string };

export const EditBannerPage = ({ banner }: any) => {
  const initialStatusObject = getInitialSelectOption(
    STATUS_OPTIONS,
    banner?.status
  );
  const initialTextColorObject = getInitialSelectOption(
    TEXT_COLOR_OPTIONS,
    banner?.textColor
  );
  const initialButtonTypeObject = getInitialSelectOption(
    BUTTON_TYPE_OPTIONS,
    banner?.buttonType,
    'none'
  );
  const initialActionObject = getInitialSelectOption(
    ACTION_OPTIONS,
    banner?.action,
    'link'
  );

  const { date: initialStartDate, time: initialStartTime } = parseDateTime(
    banner?.dateStart
  );
  const { date: initialEndDate, time: initialEndTime } = parseDateTime(
    banner?.dateEnd
  );

  const { control, handleSubmit, setValue, formState, watch, getValues } =
    useForm<EditBannerForm>({
      defaultValues: {
        title: banner?.title || '',
        startDate: initialStartDate,
        startTime: initialStartTime,
        endDate: initialEndDate,
        endTime: initialEndTime,
        heading: banner?.heading || '',
        description: banner?.description || '',
        buttonType: initialButtonTypeObject,
        buttonText: banner?.buttonText || '',
        image: banner?.image || '',
        textColor: initialTextColorObject,
        bgColor: banner?.bgColor || '',
        action: initialActionObject,
        link: banner?.link || '',
        modal: {
          title: banner?.modal?.title || '',
          description: banner?.modal?.description || '',
          content: banner?.modal?.content || '',
          buttonText: banner?.modal?.buttonText || '',
          image: banner?.modal?.image || '',
          buttonTextColor: banner?.modal?.buttonTextColor || '',
          buttonColor: banner?.modal?.buttonColor || '',
          link: banner?.modal?.link || '',
        },
        isPromotional: banner?.isPromotional || false,
        status: initialStatusObject,
        promotionalText: banner?.promotionalText || '',
      },
      mode: 'onChange',
    });
  const dispatch = useAppDispatch();
  const notify = useNotification();
  const navigate = useNavigate();

  console.log(formState);

  const watchedTextColor = watch('textColor');
  const watchedButtonType = watch('buttonType');
  const watchedAction = watch('action');
  const watchedStatus = watch('status');
  const watchedIsPromotional = watch('isPromotional');

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (banner) {
      if (banner.dateStart) {
        const { date, time } = parseDateTime(banner.dateStart);
        setValue('startDate', date);
        setValue('startTime', time);
      }
      if (banner.dateEnd) {
        const { date, time } = parseDateTime(banner.dateEnd);
        setValue('endDate', date);
        setValue('endTime', time);
      }
      Object.entries(banner).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (key === 'status') {
            setValue(
              'status',
              STATUS_OPTIONS.find((opt) => opt.id === value) || null
            );
          } else if (key === 'textColor') {
            setValue(
              'textColor',
              TEXT_COLOR_OPTIONS.find((opt) => opt.id === value) || null
            );
          } else if (key === 'buttonType') {
            setValue(
              'buttonType',
              BUTTON_TYPE_OPTIONS.find((opt) => opt.id === value) || null
            );
          } else if (key === 'action') {
            setValue(
              'action',
              ACTION_OPTIONS.find((opt) => opt.id === value) || null
            );
          } else if (key !== 'dateStart' && key !== 'dateEnd') {
            setValue(key as keyof EditBannerForm, value as any);
          }
        }
      });
      if (banner.status === undefined || banner.status === null) {
        setValue('status', null);
      }
      if (banner.textColor === undefined || banner.textColor === null) {
        setValue('textColor', null);
      }
      if (banner.buttonType === undefined || banner.buttonType === null) {
        setValue(
          'buttonType',
          BUTTON_TYPE_OPTIONS.find((opt) => opt.id === 'none') || null
        );
      }
      if (banner.action === undefined || banner.action === null) {
        setValue(
          'action',
          ACTION_OPTIONS.find((opt) => opt.id === 'link') || null
        );
      }
    } else {
      setValue('title', '');
      setValue('status', null);
      setValue('textColor', null);
      setValue(
        'buttonType',
        BUTTON_TYPE_OPTIONS.find((opt) => opt.id === 'none') || null
      );
      setValue(
        'action',
        ACTION_OPTIONS.find((opt) => opt.id === 'link') || null
      );
      setValue('startDate', '');
      setValue('startTime', '');
      setValue('endDate', '');
      setValue('endTime', '');
    }
  }, [banner, setValue]);

  const onSubmit = async (data: EditBannerForm) => {
    try {
      const dateStart = toISODate(data.startDate, data.startTime);
      const dateEnd = toISODate(data.endDate, data.endTime);
      if (!dateStart) {
        notify({ value: 'Дата старта обязательна', variant: 'error' });
        return;
      }
      const payload = {
        ...data,
        buttonType:
          data.buttonType && data.buttonType.id !== 'none'
            ? (String(data.buttonType.id) as ButtonType)
            : null,
        action: data.action
          ? (String(data.action.id) as ActionType)
          : undefined,
        status: data.status
          ? (String(data.status.id) as IBanner['status'])
          : undefined,
        modal: data.action?.id === 'modal' ? data.modal : undefined,
        link: data.action?.id === 'link' ? data.link : undefined,
        dateStart,
        ...(dateEnd ? { dateEnd } : {}),
        heading: data.heading,
      } as any;
      payload.textColor = data.textColor
        ? String(data.textColor.id)
        : undefined;
      payload.backgroundColor = data.bgColor || undefined;
      await dispatch(editBanner({ id: banner.id, data: payload })).unwrap();
      notify({ value: 'Изменения сохранены', variant: 'success' });
      navigate('/banners');
    } catch (e: any) {
      notify({
        value: e?.message || 'Ошибка при сохранении',
        variant: 'error',
      });
    }
  };

  const handleSave = () => {
    onSubmit(getValues());
  };

  const handlePublish = () => {
    if (!formState.isValid) return;
    const values = getValues();
    onSubmit({ ...values, status: { id: 'published', title: 'Опубликовано' } });
  };

  const handleUpdate = () => {
    if (!formState.isValid) return;
    const values = getValues();
    onSubmit(values);
  };

  const isHidden = watchedStatus?.id === 'hidden';

  return (
    <>
      <Header />
      <div className='flex min-h-screen animate-load-screen flex-col bg-gray100 pb-8 pt-10 transition'>
        <div className='container mt-[32px]'>
          <div className='flex gap-[32px]'>
            <aside>
              <div className='sticky left-0 top-[168px] flex w-[280px] flex-col'>
                <div className='absolute top-[-48px]'>
                  <GoBack />
                </div>
                {isHidden ? (
                  <div className='flex flex-col gap-4'>
                    <button
                      type='button'
                      style={{
                        backgroundColor: formState.isValid
                          ? '#FFD662'
                          : '#FBF3DE',
                        color: !formState.isValid ? '#ABABAB' : '#000',
                        width: 280,
                        height: 80,
                        borderRadius: 100,
                        gap: 12,
                        paddingTop: 26,
                        paddingRight: 48,
                        paddingBottom: 28,
                        paddingLeft: 48,
                      }}
                      className={`rounded-[100px] px-[48px] py-4 text-center text-xl font-medium not-italic leading-[26px] transition-all
                        ${
                          !formState.isValid
                            ? 'cursor-not-allowed opacity-100'
                            : 'hover:bg-yellow200'
                        }`}
                      disabled={!formState.isValid}
                      onClick={handlePublish}
                    >
                      Опубликовать
                    </button>
                    <button
                      type='button'
                      style={{
                        backgroundColor: formState.isDirty
                          ? '#337CEC'
                          : '#DEE9FB',
                        color: '#fff',
                        width: 280,
                        height: 80,
                        borderRadius: 100,
                        gap: 12,
                        paddingTop: 26,
                        paddingRight: 48,
                        paddingBottom: 28,
                        paddingLeft: 48,
                      }}
                      className={`rounded-[100px] px-[48px] py-4 text-center text-xl font-medium not-italic leading-[26px] transition-all
                        ${
                          !formState.isDirty
                            ? 'cursor-not-allowed opacity-100'
                            : 'hover:bg-blue-600'
                        }`}
                      disabled={!formState.isDirty}
                      onClick={handleSave}
                    >
                      Сохранить
                    </button>
                  </div>
                ) : (
                  <button
                    type='button'
                    style={{
                      backgroundColor:
                        formState.isDirty || !formState.isValid
                          ? '#FFD662'
                          : '#FBF3DE',
                      color:
                        !formState.isDirty || !formState.isValid
                          ? '#ABABAB'
                          : '#000',
                      width: 280,
                      height: 80,
                      borderRadius: 100,
                      gap: 12,
                      paddingTop: 26,
                      paddingRight: 48,
                      paddingBottom: 28,
                      paddingLeft: 48,
                    }}
                    className={`rounded-[100px] px-[48px] py-4 text-center text-xl font-medium not-italic leading-[26px] transition-all
                      ${
                        !formState.isDirty || !formState.isValid
                          ? 'cursor-not-allowed opacity-100'
                          : 'hover:bg-yellow200'
                      }`}
                    disabled={!formState.isDirty || !formState.isValid}
                    onClick={handleUpdate}
                  >
                    Обновить
                  </button>
                )}
              </div>
            </aside>
            <div className='w-full rounded-[32px] bg-white p-[40px]'>
              <form onSubmit={handleSubmit(onSubmit)}>
                <fieldset className='mb-[40px] flex w-[100%] items-center justify-between'>
                  <h1 className='text-[42px] font-medium leading-[48px] text-black'>
                    Описание
                  </h1>
                </fieldset>

                <div className='flex flex-col gap-[32px]'>
                  <Controller
                    name='title'
                    control={control}
                    rules={{ required: 'Введите название' }}
                    render={({ field }) => (
                      <Input
                        htmlFor='title'
                        title='Название'
                        required
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value)}
                        onClear={() => field.onChange('')}
                        isClose={!!field.value}
                        error={formState.errors.title?.message}
                      />
                    )}
                  />
                  <div className='flex gap-[32px]'>
                    <div className='flex w-1/2 flex-col gap-[8px]'>
                      <div className='relative'>
                        <Controller
                          name='startDate'
                          control={control}
                          rules={{ required: 'Выберите дату старта' }}
                          render={({ field }) => (
                            <DateInput
                              label='Дата старта'
                              value={field.value || ''}
                              onChange={field.onChange}
                              required
                              inputRef={startDateRef}
                              error={formState.errors.startDate?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className='flex w-1/2 flex-col gap-[8px]'>
                      <div>
                        <Controller
                          name='startTime'
                          control={control}
                          render={({ field }) => (
                            <TimeInput
                              label='Время старта'
                              value={field.value || ''}
                              onChange={field.onChange}
                              required={false}
                              error={formState.errors.startTime?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className='flex gap-[32px]'>
                    <div className='flex w-1/2 flex-col gap-[8px]'>
                      <div className='relative'>
                        <Controller
                          name='endDate'
                          control={control}
                          render={({ field }) => (
                            <DateInput
                              label='Дата завершения'
                              value={field.value || ''}
                              onChange={field.onChange}
                              required={false}
                              inputRef={endDateRef}
                              error={formState.errors.endDate?.message}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <div className='flex w-1/2 flex-col gap-[8px]'>
                      <label className='w-fit text-[20px] font-medium leading-[26px]'>
                        Время завершения
                      </label>
                      <div>
                        <Controller
                          name='endTime'
                          control={control}
                          render={({ field }) => (
                            <input
                              type='time'
                              className='h-[72px] w-full rounded-2xl border border-solid border-[#E1E1E1] px-6 text-xl font-normal leading-[26px] outline-none'
                              value={field.value || ''}
                              onChange={field.onChange}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <Controller
                    name='heading'
                    control={control}
                    render={({ field }) => (
                      <Input
                        htmlFor='heading'
                        title='Заголовок'
                        value={field.value || ''}
                        onChange={field.onChange}
                        onClear={() => field.onChange('')}
                        isClose={!!field.value}
                        error={formState.errors.heading?.message}
                      />
                    )}
                  />
                  <Controller
                    name='description'
                    control={control}
                    render={({ field }) => (
                      <TextArea
                        label='Описание'
                        maxLength={300}
                        value={field.value || ''}
                        onChange={field.onChange}
                        length={field.value?.length || 0}
                      />
                    )}
                  />
                  <div className='flex gap-[32px]'>
                    <div className='w-1/2'>
                      <Select
                        label='Кнопка'
                        values={BUTTON_TYPE_OPTIONS}
                        selected={watchedButtonType || undefined}
                        handleSelect={(selectedOption: SelectOption) =>
                          setValue('buttonType', selectedOption, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        placeholder={
                          watchedButtonType
                            ? watchedButtonType.title
                            : 'Выберите'
                        }
                      />
                    </div>
                    {watchedButtonType?.id === 'text' && (
                      <div className='w-1/2'>
                        <Controller
                          name='buttonText'
                          control={control}
                          rules={{ required: 'Введите текст кнопки' }}
                          render={({ field }) => (
                            <Input
                              htmlFor='buttonText'
                              required
                              title='Текст в кнопке'
                              value={field.value || ''}
                              onChange={field.onChange}
                              onClear={() => field.onChange('')}
                              isClose={!!field.value}
                              error={formState.errors.buttonText?.message}
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                  <Controller
                    name='image'
                    control={control}
                    render={({ field }) => (
                      <InputBannerImage
                        value={field.value}
                        onChange={field.onChange}
                        error={formState.errors.image?.message}
                      />
                    )}
                  />
                  <div className='flex gap-[32px]'>
                    <div className='w-1/2'>
                      <Select
                        label='Цвет текста'
                        values={TEXT_COLOR_OPTIONS}
                        selected={watchedTextColor || undefined}
                        handleSelect={(selectedOption: SelectOption) =>
                          setValue('textColor', selectedOption, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                        placeholder={
                          watchedTextColor ? watchedTextColor.title : 'Выберите'
                        }
                      />
                    </div>
                    <div className='w-1/2'>
                      <Controller
                        name='bgColor'
                        control={control}
                        render={({ field }) => (
                          <Input
                            htmlFor='bgColor'
                            title='Цвет фона'
                            value={field.value || ''}
                            onChange={field.onChange}
                            onClear={() => field.onChange('')}
                            isClose={!!field.value}
                            error={formState.errors.bgColor?.message}
                            className='w-1/2'
                            placeholder='#'
                            pattern='^#[0-9A-Fa-f]{6}$'
                          />
                        )}
                      />
                    </div>
                  </div>
                  <Controller
                    name='action'
                    control={control}
                    render={({ field }) => (
                      <>
                        <Select
                          label='Действие'
                          values={ACTION_OPTIONS}
                          selected={field.value || undefined}
                          handleSelect={(option) => field.onChange(option)}
                          placeholder={
                            field.value ? field.value.title : 'Выберите'
                          }
                        />
                        {formState.errors.action?.message && (
                          <div className='text-red-500 mt-1 text-sm'>
                            {formState.errors.action.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                  {watchedAction?.id === 'link' && (
                    <Controller
                      name='link'
                      control={control}
                      rules={{ required: 'Введите ссылку' }}
                      render={({ field }) => (
                        <Input
                          htmlFor='link'
                          title='Ссылка'
                          required
                          value={field.value || ''}
                          onChange={field.onChange}
                          onClear={() => field.onChange('')}
                          isClose={!!field.value}
                          error={formState.errors.link?.message}
                        />
                      )}
                    />
                  )}
                  {watchedAction?.id === 'modal' && (
                    <div className='flex flex-col gap-[16px]'>
                      <Controller
                        name='modal.title'
                        control={control}
                        render={({ field }) => (
                          <Input
                            htmlFor='modal.title'
                            title='Заголовок'
                            value={field.value || ''}
                            onChange={field.onChange}
                            onClear={() => field.onChange('')}
                            isClose={!!field.value}
                            error={formState.errors.modal?.title?.message}
                          />
                        )}
                      />
                      <Controller
                        name='modal.description'
                        control={control}
                        render={({ field }) => (
                          <Input
                            htmlFor='modal.description'
                            title='Описание'
                            value={field.value || ''}
                            onChange={field.onChange}
                            onClear={() => field.onChange('')}
                            isClose={!!field.value}
                            error={formState.errors.modal?.description?.message}
                          />
                        )}
                      />

                      <Controller
                        name='modal.buttonText'
                        control={control}
                        rules={{ required: 'Введите текст кнопки' }}
                        render={({ field }) => (
                          <Input
                            htmlFor='modal.buttonText'
                            title='Текст в кнопке'
                            required
                            value={field.value || ''}
                            onChange={field.onChange}
                            onClear={() => field.onChange('')}
                            isClose={!!field.value}
                            error={formState.errors.modal?.buttonText?.message}
                          />
                        )}
                      />
                      <div className='flex gap-[32px]'>
                        <div className='w-1/2'>
                          <Controller
                            name='bgColor'
                            control={control}
                            rules={{ required: 'Выберите цвет кнопки' }}
                            render={({ field }) => (
                              <Input
                                htmlFor='modal.buttonColor'
                                title='Цвет кнопки'
                                required
                                value={field.value || ''}
                                onChange={field.onChange}
                                onClear={() => field.onChange('')}
                                isClose={!!field.value}
                                error={
                                  formState.errors.modal?.buttonColor?.message
                                }
                                className='w-1/2'
                                placeholder='#'
                                pattern='^#[0-9A-Fa-f]{6}$'
                              />
                            )}
                          />
                        </div>
                        <div className='w-1/2'>
                          <Controller
                            name='modal.buttonTextColor'
                            control={control}
                            rules={{
                              required: 'Выберите цвет текста в кнопке',
                            }}
                            render={({ field }) => (
                              <div>
                                <Select
                                  label='Цвет текста в кнопке'
                                  values={TEXT_COLOR_OPTIONS}
                                  selected={
                                    TEXT_COLOR_OPTIONS.find(
                                      (opt) => opt.id === field.value
                                    ) || undefined
                                  }
                                  handleSelect={(
                                    selectedOption: SelectOption
                                  ) => field.onChange(selectedOption.id)}
                                  placeholder={
                                    TEXT_COLOR_OPTIONS.find(
                                      (opt) => opt.id === field.value
                                    )?.title || 'Выберите'
                                  }
                                  required
                                />
                                {formState.errors.modal?.buttonTextColor
                                  ?.message && (
                                  <span className='text-red-500 mt-1 text-sm'>
                                    {
                                      formState.errors.modal?.buttonTextColor
                                        ?.message
                                    }
                                  </span>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      </div>

                      <Controller
                        name='modal.link'
                        control={control}
                        rules={{ required: 'Введите ссылку' }}
                        render={({ field }) => (
                          <Input
                            htmlFor='modal.link'
                            title='Ссылка'
                            required
                            value={field.value || ''}
                            onChange={field.onChange}
                            onClear={() => field.onChange('')}
                            isClose={!!field.value}
                            error={formState.errors.modal?.link?.message}
                          />
                        )}
                      />
                      <Controller
                        name='modal.image'
                        control={control}
                        render={({ field }) => (
                          <InputModalImage
                            value={field.value}
                            onChange={field.onChange}
                            error={formState.errors.modal?.image?.message}
                          />
                        )}
                      />
                    </div>
                  )}
                  <Controller
                    name='isPromotional'
                    control={control}
                    render={({ field }) => (
                      <CustomCheckbox
                        isOpen={field.value}
                        onClick={(val) => field.onChange(val)}
                        id='isPromotional'
                        label='Рекламный материал'
                      />
                    )}
                  />
                  {watchedIsPromotional && (
                    <Controller
                      name='promotionalText'
                      control={control}
                      render={({ field }) => (
                        <Input
                          htmlFor='promotionalText'
                          title='Реклама'
                          value={field.value || ''}
                          onChange={field.onChange}
                          onClear={() => field.onChange('')}
                          isClose={!!field.value}
                          error={formState.errors.promotionalText?.message}
                          helperText='ООО «Компания», ИНН: 0000000000, erid: 2VtzqvpU3PR'
                        />
                      )}
                    />
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
